import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  Home, 
  Info, 
  BarChart3, 
  Settings, 
  Mail,
  Menu,
  X,
  LogIn,
  LogOut,
  Shield,
  User,
  Lock,
  Target,
  Presentation,
  Newspaper,
  Trophy
} from "lucide-react";
import { WORLD_CUP_OFFICIAL_LOGO } from "@/lib/worldCup2026";
import { WorldCupLogo } from "@/components/world-cup/WorldCupLogo";

import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { LanguageSelector } from "@/components/ui/language-selector";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, isAdmin, isModerator, signOut } = useAuth();
  const { t } = useTranslation('navigation');

  const navigationItems = [
    { name: t('home'), href: "/", icon: Home },
    { name: "World Cup 2026", href: "/world-cup", icon: Trophy, logo: WORLD_CUP_OFFICIAL_LOGO },
    { name: t('labs'), href: "/labs", icon: BarChart3 },
    { name: "SG7 Picks", href: "/sg7-picks", icon: Target },
    { name: "SG7 Journal", href: "/journal", icon: Newspaper },
    { name: t('about'), href: "/sobre", icon: Info },
    { name: t('contact'), href: "/contato", icon: Mail },
  ];

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 bg-blueprint-bg text-blueprint-text rounded sketch-border hover-sketch"
      >
        {isOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-full w-64 blueprint-bg border-r-2 border-blueprint-lines transition-transform duration-300 z-40",
          "lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
          className
        )}
      >
        <div className="p-6 pl-16 lg:pl-6 border-b border-blueprint-lines">
          <h2 className="text-sketch-lg text-blueprint-text text-sketch">
            {t('appName', { ns: 'common' })}
          </h2>
          <p className="text-sm text-blueprint-text/70 mt-1 font-medium">
            {t('appFullName', { ns: 'common' })}
          </p>
        </div>

        {/* Language Selector */}
        <div className="p-4 border-b border-blueprint-lines flex justify-center">
          <LanguageSelector />
        </div>

        {/* User Section */}
        {user && (
          <div className="p-4 border-b border-blueprint-lines">
            <button
              onClick={() => {
                navigate('/perfil');
                setIsOpen(false);
              }}
              className="flex items-center gap-3 mb-3 w-full hover:bg-blueprint-lines/30 p-2 rounded sketch-border transition-colors cursor-pointer"
            >
              <Avatar className="h-10 w-10 border-2 border-blueprint-lines">
                <AvatarFallback className="bg-blueprint-text/10 text-blueprint-text font-bold">
                  {profile?.username?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-bold text-blueprint-text truncate">
                  {profile?.username || user.email?.split('@')[0]}
                </p>
                <p className="text-xs text-blueprint-text/50 truncate">
                  {user.email}
                </p>
              </div>
            </button>

            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 sketch-border hover-sketch"
                  >
                    <Settings size={14} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="start" 
                  className="w-48 sketch-border border-blueprint-lines z-[100] shadow-xl bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm"
                >
                  <DropdownMenuItem 
                    onClick={() => {
                      navigate('/perfil');
                      setIsOpen(false);
                    }}
                    className="cursor-pointer text-blueprint-text hover:bg-blueprint-lines"
                  >
                    <User size={14} className="mr-2" />
                    {t('profile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => {
                      navigate('/definicoes');
                      setIsOpen(false);
                    }}
                    className="cursor-pointer text-blueprint-text hover:bg-blueprint-lines"
                  >
                    <Lock size={14} className="mr-2" />
                    {t('settings')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-blueprint-lines" />
                  <DropdownMenuItem 
                    onClick={signOut}
                    className="cursor-pointer text-blueprint-text hover:bg-blueprint-lines"
                  >
                    <LogOut size={14} className="mr-2" />
                    {t('signOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                onClick={signOut}
                variant="outline"
                size="sm"
                className="flex-1 sketch-border hover-sketch"
              >
                <LogOut size={14} className="mr-2" />
                {t('signOut')}
              </Button>
            </div>
          </div>
        )}

        {!user && (
          <div className="p-4 border-b border-blueprint-lines">
            <Button
              onClick={() => window.location.href = '/auth'}
              variant="default"
              size="sm"
              className="w-full sketch-border hover-sketch font-bold"
            >
              <LogIn size={14} className="mr-2" />
              {t('signIn')}
            </Button>
          </div>
        )}

        <nav className="p-4 space-y-2">
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;
            const isWorldCup = (item as any).logo === WORLD_CUP_OFFICIAL_LOGO;

            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-3 rounded sketch-border text-sm transition-colors hover-sketch",
                  isActive
                    ? "bg-blueprint-text text-blueprint-bg font-bold"
                    : "text-blueprint-text hover:bg-blueprint-lines"
                )}
              >
                {isWorldCup ? (
                  <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded bg-white">
                    <WorldCupLogo size={24} />
                  </span>
                ) : (
                  <Icon size={18} className="shrink-0" />
                )}
                <span className="font-bold">{item.name}</span>
              </NavLink>
            );

          })}

          {/* Admin Links - visible for admins AND moderators (moderators see a filtered Admin page) */}
          {(isAdmin || isModerator) && (
            <NavLink
              to="/admin"
              onClick={() => setIsOpen(false)}
              className={cn(
                "flex items-center gap-3 px-3 py-3 rounded sketch-border text-sm transition-colors hover-sketch",
                location.pathname === '/admin'
                  ? "bg-blueprint-text text-blueprint-bg font-bold"
                  : "text-blueprint-text hover:bg-blueprint-lines"
              )}
            >
              <Shield size={18} className="shrink-0" />
              <span className="font-bold">{t('admin')}</span>
            </NavLink>
          )}

          {/* Moderator Links - visible for admins AND moderators */}
          {(isAdmin || isModerator) && (
            <>
              <NavLink
                to="/admin/picks-performance"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-3 rounded sketch-border text-sm transition-colors hover-sketch",
                  location.pathname === '/admin/picks-performance'
                    ? "bg-blueprint-text text-blueprint-bg font-bold"
                    : "text-blueprint-text hover:bg-blueprint-lines"
                )}
              >
                <Target size={18} className="shrink-0" />
                <span className="font-bold">Picks Performance</span>
              </NavLink>
              
              <NavLink
                to="/admin/pitch"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-3 rounded sketch-border text-sm transition-colors hover-sketch",
                  location.pathname === '/admin/pitch'
                    ? "bg-blueprint-text text-blueprint-bg font-bold"
                    : "text-blueprint-text hover:bg-blueprint-lines"
                )}
              >
                <Presentation size={18} className="shrink-0" />
                <span className="font-bold">Pitch Deck</span>
              </NavLink>
            </>
          )}
        </nav>

        {/* Blueprint decorative elements */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="border border-blueprint-lines p-2 rounded">
            <div className="text-xs text-blueprint-text/50 font-mono">
              {t('version')} 7.0.1 | BETA
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}